import {
  AvKeyValue,
  ConditionType,
  customPreset,
  getEndOfDay,
  getExpression,
  getStartOfDay,
  isDateRangeValue,
  operatorColors,
  Preset,
  RelativeDatePeriod,
  RelativeDateType,
  StringConditionType,
  ValueWithPreset,
} from '@fabric-ui/components';
import { FormatterType } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';
import { format } from 'date-fns';
import { IconVariant, ItemWithLogo } from '../../components/ItemWithLogo';
import { LogsQuery } from './types';

export const generateTempId = () => `tempId_${crypto.randomUUID()}`;
export const isTempId = (id: string) => id.startsWith('tempId_');

export const sanitizeUnicode = (content: string) => content.replace(/[^(\n|\x20-\x7E)]/g, '');

const regex = /\b(AND|OR)\b/g; // Matches the words "AND" or "OR" as whole words

export const QueryText = ({ query }: { query: string }) =>
  query.split(regex).map((part, i) => {
    if (part === 'AND' || part === 'OR') {
      const key = `${query}-${i}`;
      return (
        <Box key={key} component="span" sx={{ color: operatorColors[part.toLowerCase()] }}>
          {part}
        </Box>
      );
    }
    return part;
  });

const legalIntervals = [
  { display: '1 Seconds', ms: 1, amount: 1, unit: 'SECOND' },
  { display: '1 Seconds', ms: 1000, amount: 1, unit: 'SECOND' },
  { display: '2 Seconds', ms: 2000, amount: 2, unit: 'SECOND' },
  { display: '3 Seconds', ms: 3000, amount: 3, unit: 'SECOND' },
  { display: '5 Seconds', ms: 5000, amount: 5, unit: 'SECOND' },
  { display: '10 Seconds', ms: 10000, amount: 10, unit: 'SECOND' },
  { display: '20 Seconds', ms: 20000, amount: 20, unit: 'SECOND' },
  { display: '30 Seconds', ms: 30000, amount: 30, unit: 'SECOND' },
  { display: '1 Minutes', ms: 60000, amount: 1, unit: 'MINUTE' },
  { display: '2 Minutes', ms: 120000, amount: 2, unit: 'MINUTE' },
  { display: '3 Minutes', ms: 180000, amount: 3, unit: 'MINUTE' },
  { display: '5 Minutes', ms: 300000, amount: 5, unit: 'MINUTE' },
  { display: '10 Minutes', ms: 600000, amount: 10, unit: 'MINUTE' },
  { display: '20 Minutes', ms: 1200000, amount: 20, unit: 'MINUTE' },
  { display: '30 Minutes', ms: 1800000, amount: 30, unit: 'MINUTE' },
  { display: '1 Hours', ms: 3600000, amount: 1, unit: 'HOUR' },
  { display: '2 Hours', ms: 7200000, amount: 2, unit: 'HOUR' },
  { display: '3 Hours', ms: 10800000, amount: 3, unit: 'HOUR' },
  { display: '8 Hours', ms: 28800000, amount: 8, unit: 'HOUR' },
  { display: '12 Hours', ms: 43200000, amount: 12, unit: 'HOUR' },
  { display: '1 Days', ms: 86400000, amount: 1, unit: 'DAY' },
  { display: '2 Days', ms: 172800000, amount: 2, unit: 'DAY' },
  { display: '3 Days', ms: 259200000, amount: 3, unit: 'DAY' },
  { display: '7 Days', ms: 604800000, amount: 7, unit: 'DAY' },
  { display: '30 Days', ms: 2592000000, amount: 30, unit: 'DAY' },
] as const;
export type LegalIntervalUnit = (typeof legalIntervals)[number]['display'] extends `${number} ${infer W}` ? W : never;

export const getInterval = (maxHistogramBuckets, timeRange) => {
  const rangeMs = timeRange[1].getTime() - timeRange[0].getTime();
  const interval = legalIntervals.find(interval => rangeMs / interval.ms <= maxHistogramBuckets);
  return interval || legalIntervals[legalIntervals.length - 1];
};

export const fillHistogramDataGap = ([from, to]: [Date, Date], intervalMs: number, rows: { count: number; date: string }[]) => {
  const startMs = from.getTime();
  const endMs = to.getTime();
  if (!(endMs >= startMs) || intervalMs <= 0) {
    return [];
  }
  const totalBuckets = Math.floor((endMs - startMs) / intervalMs) + 1;
  const byBucket = rows.reduce((map, row) => {
    const timestamp = new Date(row.date).getTime();
    const index = Math.floor((timestamp - startMs) / intervalMs);
    if (index >= 0 && index < totalBuckets) {
      const b = startMs + index * intervalMs;
      map.set(b, (map.get(b) ?? 0) + Number(row.count ?? 0));
    }
    return map;
  }, new Map<number, number>());
  return Array.from({ length: totalBuckets }, (_, i) => {
    const ts = startMs + i * intervalMs;
    return { date: new Date(startMs + i * intervalMs), count: byBucket.get(ts) ?? 0 };
  });
};

const now = new Date();

export const maxTabs = 10;
export const newDefaultDateRange = [getStartOfDay(now), getEndOfDay(now)];

export const defaultPreset: Preset = { type: RelativeDateType.last, count: 1, period: RelativeDatePeriod.days };

// TODO: viewProjections HEAD CELLS
export const defaultHeadCells = [
  {
    id: 'avalorRowId',
    label: 'ID',
    hidden: true,
    isKey: true,
    disableSortBy: true,
  },
  {
    id: 'timestamp',
    type: FormatterType.date,
    label: 'Time',
    initWidth: 142,
    style: { '> div': { whiteSpace: 'nowrap' } },
    formatter: (v, { timestamp, data }) => (
      <Box sx={{ ...flex.row, fontSize: '13px', gap: '8px' }}>{format(new Date(timestamp || data.timestamp), 'MMM dd kk:mm:ss')}</Box>
    ),
    disableSortBy: true,
  },
  {
    id: 'source',
    label: 'Source',
    formatter: (v, { data: { source } }) => <ItemWithLogo variant={IconVariant.sourcesMapByName} type={source} />,
    disableSortBy: true,
  },
  {
    id: 'data',
    label: 'Logs',
    sx: { width: '100%' },
    style: { '> div': { maxWidth: 'none' } },
    formatter: (_, { flatten }) => <AvKeyValue value={flatten} linesLimit={2} />,
    disableSortBy: true,
  },
];

const Untitled = 'Untitled';

export const getHeadCellToExport = (flatData: any[]) =>
  [...new Set(flatData.reduce((acc, v) => [...acc, ...Object.keys(v)], []))].map(key => ({ id: key, label: key }));
export const getNextUntitledVal = (arr, prefixName = Untitled) => {
  const next = (arr || [])
    .reduce((acc, v) => {
      const newVal = Number(v.name.split(prefixName)[1]);
      if (v.name.includes(prefixName) && newVal) {
        return [...acc, newVal];
      }
      return acc;
    }, [])
    .sort((a, b) => a - b);
  const nextVal =
    next.reduce((acc, v, i, array) => {
      if (acc !== null) {
        return acc;
      }

      if (v !== i + 1) {
        return i + 1;
      }
      if (i === array.length - 1) {
        return i + 2;
      }
      return null;
    }, null) || 1;

  return { id: prefixName + nextVal, name: prefixName + nextVal };
};

export type QueryObj = { name: string; source: string; query: string };
export const queriesList: QueryObj[] = [
  {
    name: 'Exploitable CVEs',
    source: 'Threat Intel',
    query: '`source` = "Threat Intel" AND `has_exploits` = "true"',
  },
  {
    name: 'Failed Okta logins',
    source: 'Okta',
    query: '`source` = "Okta" AND `outcome.result` = "FAILURE"',
  },
  {
    name: 'Tenable open vulnerabilities',
    source: 'Tenable Vulnerabilities',
    query: '`source`= "Tenable Vulnerabilities" AND `state` = "OPEN"',
  },
  {
    name: 'Wiz active findings',
    source: 'Wiz - Vulnerability Findings',
    query: '`source` = "Wiz - Vulnerability Findings" AND `status` = "Active"',
  },
  {
    name: 'Crowdstrike critical vulnerabilities',
    source: 'Crowdstrike Vulnerabilities',
    query: '`source` = "Crowdstrike Vulnerabilities" AND `cve.severity` = "CRITICAL"',
  },
  {
    name: 'Okta logins with warnings severity',
    source: 'Okta',
    query: '`source` = "Okta" AND `severity` = "WARN"',
  },
  {
    name: 'Tenable reopened vulnerabilities',
    source: 'Tenable Vulnerabilities',
    query: '`source` = "Tenable Vulnerabilities" AND `state` = "REOPENED"',
  },
  {
    name: 'Tenable open and reopened issues',
    source: 'Tenable Issues',
    query: '`source` = "Tenable Issues" AND (`state` = "REOPENED" OR `state` = "OPEN")',
  },
  {
    name: 'Snyk high and critical issues',
    source: 'Snyk',
    query: '`source` = "Snyk" AND (`issue.severity` = "critical" OR `issue.severity` = "high")',
  },
  {
    name: 'Snyk fixable issues',
    source: 'Snyk',
    query: '`source` = "Snyk" AND `is_fixable` = "true"',
  },
  {
    name: 'Legit critical vulnerabilities',
    source: 'Legit',
    query: '`source` = "Legit" AND `severity` = "Critical"',
  },
  {
    name: 'Crowdstrike high vulnerabilities',
    source: 'Crowdstrike Vulnerabilities',
    query: '`source` = "Crowdstrike Vulnerabilities" AND `cve.severity` = "HIGH"',
  },
];

export const predefinedPresets = [
  { title: 'Last 15 Minutes', definition: { type: RelativeDateType.last, count: 15, period: RelativeDatePeriod.minutes } },
  { title: 'Last 1 Hour', definition: { type: RelativeDateType.last, count: 1, period: RelativeDatePeriod.hours } },
  { title: 'Last 4 Hours', definition: { type: RelativeDateType.last, count: 4, period: RelativeDatePeriod.hours } },
  { title: 'Last 1 Day', definition: { type: RelativeDateType.last, count: 1, period: RelativeDatePeriod.days } },
  { title: 'Last 3 Days', definition: { type: RelativeDateType.last, count: 3, period: RelativeDatePeriod.days } },
  { title: 'Last 7 Days', definition: { type: RelativeDateType.last, count: 7, period: RelativeDatePeriod.days } },
  { title: 'Last 15 Days', definition: { type: RelativeDateType.last, count: 15, period: RelativeDatePeriod.days } },
  { title: customPreset },
];

export const LibraryDialogPrefix = 'LD_';
export const DocumentObjectsPrefix = 'DOP_';
export const SpaceReplacer = '_SPACE_';
export const addPrefixToFilters = (filters, prefix = LibraryDialogPrefix) =>
  Object.keys(filters).reduce((acc, key) => {
    const newKey = `${prefix}${key}`;
    acc[newKey] = filters[key]; // Add underscore to the key
    return acc;
  }, {});
export const removePrefixFromFilters = (filters, prefix = LibraryDialogPrefix) =>
  Object.keys(filters).reduce(
    (acc, key) => {
      const newKey = key.replace(prefix, '');
      acc[newKey] = filters[key]; // Add underscore to the key
      return acc;
    },
    {} as Omit<LogsQuery, 'tableName'> & { table?: string | null }
  );
export const areSetsEquals = (a, b) => a.size === b.size && a.union(b).size === a.size;

export const getPieSlicerFilter = pieSlicer =>
  pieSlicer.length
    ? pieSlicer.map(({ fieldName, value }) =>
        ['null', null].includes(value)
          ? getExpression({
              fieldName,
              operator: StringConditionType.EMPTY,
              typeCondition: ConditionType.string,
              value: {},
            })
          : getExpression({
              fieldName,
              operator: StringConditionType.EQUALS,
              typeCondition: ConditionType.string,
              value: value.toString(),
            })
      )
    : [];

export const transformDatePayload = (p: ValueWithPreset | Date[]): [Date, Date] => {
  if (Array.isArray(p)) return [p[0], p[1]] as const;
  if (isDateRangeValue(p.value)) {
    const { from, to } = p.value;
    if (!from || !to) throw Error('Invalid date range');
    return [from, to] as const;
  }
  throw Error('Invalid date range');
};
